import { Participant } from 'app/constants/schemas';
import { getAllEntitiesOfType } from 'app/models/entities/selectors';
import { AppState } from 'app/store';
import { EntityStringId } from 'app/types/entity';
import { Map } from 'immutable';
import TrackState from 'app/services/rtc/TrackState';
import { IDeviceManagerState } from 'app/services/rtc/DeviceManager/DeviceManagerState';
import DisplayMediaSource from 'app/services/rtc/DisplayMediaSource';

export const getAllParticipants = (state: AppState): Map<EntityStringId, Participant> | undefined =>
  getAllEntitiesOfType(state, Participant);

// Pick only appData field so that we don't have to care if it's
// recordized or normalized
type ParticipantsAppData = Pick<Participant, 'appData'>;

export const isParticipantHost = (participant?: ParticipantsAppData) => {
  const host = participant?.appData?.get('host');
  return Boolean(host);
};

/**
 * Tells whether participant was upgraded to host. Returns true only
 * if the upgrade happened. Will return false if the participant
 * was host already.
 */
export const isUpgradedToHost = (
  oldParticipant: ParticipantsAppData | undefined,
  newParticipant: ParticipantsAppData,
) => {
  return !isParticipantHost(oldParticipant) && isParticipantHost(newParticipant);
};

/**
 * In Chrome (at least), when a tab that is being shared is backgrounded
 * the stream state becomes Muted. This selector treats muted stream as active
 * much like the camera stream, to make sure screen sharing is not stopped
 * whenever an idle tab or window goes Muted
 */
export const isScreenSharingSourceActive = (source: DisplayMediaSource) => {
  const { trackState } = source;

  return (
    trackState === TrackState.Active ||
    trackState === TrackState.Muted
  );
};

export const getActiveVideoDevice = (devices: IDeviceManagerState) => {
  if (isScreenSharingSourceActive(devices.screen)) {
    return devices.screen;
  }

  return devices.video;
};

export const getStreamSettings = (stream?: MediaStream) => {
  const tracks = stream ? stream.getTracks() : [];

  return tracks.map((track) => {
    if (typeof track.getSettings === 'function') {
      return track.getSettings();
    }

    return {};
  });
};
